import React, { RefObject, useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { durationToString, shortenedDateToString } from '../../time';
import { UserDataResult, UserSessionsResultItem } from '../../interfaces/services';
import { MIN_TO_SEC, SEC_TO_MS } from '../../cfg/const';
import { SessionStatus } from '../../components/SessionStatus/SessionStatus';
import { useSelector } from "react-redux";
import { RootState } from '../../store';
import { StaticFilter, convertFromReduxSafeUserState, convertFromReduxSafeVisibleAccounts, reloadPendingReviewsInitialState } from '../../lib/redux/store';
import { User2 } from 'lucide-react';
import { useSessionList, MAX_BACKOFF_RETRIES, MAX_DATE_LOOKBACK_MONTHS } from '../../lib/redux/sessions/list-hook';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SessionDetailView, viewEnforcesStart } from '../SessionDetails/types';

export type SessionListProps = {
  onRowClick: (item: UserSessionsResultItem) => void;
  shownSessionId?: string;
  forcedFilter?: StaticFilter;
  forUser?: boolean;
  view: SessionDetailView;
}

export function SessionList(props: SessionListProps) {
  const _listRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // Track timeouts more systematically to ensure cleanup
  const timeoutsRef = useRef<{[key: string]: NodeJS.Timeout}>({});
  
  const clearTimeout = useCallback((key: string) => {
    if (timeoutsRef.current[key]) {
      global.clearTimeout(timeoutsRef.current[key]);
      delete timeoutsRef.current[key];
    }
  }, []);
  
  const setTimeout = useCallback((callback: () => void, delay: number, key: string) => {
    // Clear existing timeout with the same key if it exists
    clearTimeout(key);
    // Set new timeout
    timeoutsRef.current[key] = global.setTimeout(callback, delay);
    return timeoutsRef.current[key];
  }, [clearTimeout]);
  
  // Clean up all timeouts on unmount
  useEffect(() => {
    return () => {
      Object.keys(timeoutsRef.current).forEach(key => {
        global.clearTimeout(timeoutsRef.current[key]);
      });
    };
  }, []);
  
  const dispatch = useDispatch();
  
  // Get user and visible accounts directly from Redux
  const user = useSelector((state: RootState) => convertFromReduxSafeUserState(state.user));
  const visibleAccounts = useSelector((state: RootState) => 
    convertFromReduxSafeVisibleAccounts(state.visibleAccounts, (u: UserDataResult) => u.team_is_active && u.can_dial)
  )

  // Use the session list hook with proper memoization to prevent re-renders
  const { items, isLoading, hasMore, loadMore, refresh, noResultsAfterBackoff, monthsSearched, hasStrictStartDate } = useSessionList({ 
    forcedFilter: props.forcedFilter, 
    forcedStart: viewEnforcesStart(props.view), 
    forUser: props.forUser,
  });

  // Critical: Use refs to track loading state to avoid dependency issues and stale closures
  const isLoadingRef = useRef<boolean>(isLoading);
  const hasMoreRef = useRef<boolean>(hasMore);
  const itemsRef = useRef<UserSessionsResultItem[]>(items);
  
  // Update refs when values change to ensure current values are used in callbacks
  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
    itemsRef.current = items;
  }, [isLoading, hasMore, items]);
  
  // Track fill attempts with a ref to avoid unwanted re-renders
  const fillAttemptsRef = useRef<number>(0);
  const MAX_FILL_ATTEMPTS = 5;

  // Group items by date - items are already sorted in the hook
  const datesToItems = useMemo(() => {
    if (!items.length) return [];
    
    const dates = Array.from(new Set(items.map(item => 
      shortenedDateToString(item.scheduled_start)
    )));

    return dates.map(date => ({
      date,
      items: items.filter(item => 
        shortenedDateToString(item.scheduled_start) === date
      ),
    }));
  }, [items]);

  // Only flatten items once they've been grouped by date
  const flattenedItems = useMemo(() => {
    return datesToItems.flatMap(dateGroup => [
      { type: 'header' as const, date: dateGroup.date },
      ...dateGroup.items.map(item => ({ type: 'item' as const, data: item }))
    ]);
  }, [datesToItems]);
  
  // Track virtualization readiness
  const [virtualizerReady, setVirtualizerReady] = useState(false);
  
  // Set up virtualizer with improved config and more stable references
  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => _listRef.current,
    estimateSize: useCallback((index) => {
      const item = flattenedItems[index];
      if (!item) return 56; // Default height as fallback
      return item.type === 'header' ? 32 : 56;
    }, [flattenedItems]),
    overscan: 20, // Keep a good number of items rendered
    getItemKey: useCallback((index: number) => {
      const item = flattenedItems[index];
      if (!item) return `item-${index}`;
      return item.type === 'header' 
        ? `header-${item.date}` 
        : `item-${item.data.session_id || index}`;
    }, [flattenedItems]),
  });

  // Effect to set virtualizer as ready after initial render
  useEffect(() => {
    if (rowVirtualizer && flattenedItems.length > 0 && !virtualizerReady) {
      setTimeout(() => {
        setVirtualizerReady(true);
      }, 200, 'virtualizerReadyTimer');
      
      return () => clearTimeout('virtualizerReadyTimer');
    }
  }, [rowVirtualizer, flattenedItems.length, virtualizerReady, setTimeout, clearTimeout]);

  // Improved sticky header handling with IntersectionObserver
  const [visibleHeaders, setVisibleHeaders] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const headerObserverCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    setVisibleHeaders(prev => {
      const next = new Set(prev);
      entries.forEach(entry => {
        const date = entry.target.getAttribute('data-date');
        if (!date) return;
        
        if (entry.isIntersecting) {
          next.add(date);
        } else {
          next.delete(date);
        }
      });
      return next;
    });
  }, []);

  // Set up intersection observer for headers
  useEffect(() => {
    if (!_listRef.current) return;

    observerRef.current = new IntersectionObserver(headerObserverCallback, {
      root: _listRef.current,
      threshold: [0, 1],
      rootMargin: '-1px 0px 0px 0px' // Slight offset to handle border
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [headerObserverCallback]);

  // Header reference callback to observe headers
  const headerRefCallback = useCallback((element: HTMLElement | null, date: string) => {
    if (!element || !observerRef.current) return;
    
    element.setAttribute('data-date', date);
    observerRef.current.observe(element);
    
    return () => {
      if (observerRef.current) {
        observerRef.current.unobserve(element);
      }
    };
  }, []);

  // Simplified scroll handler focused on checking if we need to load more
  const handleInfiniteScroll = useCallback(() => {
    // Exit early if necessary conditions aren't met
    if (!_listRef.current || isLoadingRef.current || !hasMoreRef.current || !rowVirtualizer) {
      return;
    }

    const listElement = _listRef.current;
    const scrollPosition = listElement.scrollTop;
    const viewportHeight = listElement.clientHeight;
    const totalHeight = rowVirtualizer.getTotalSize();
    
    if (totalHeight <= 0 || viewportHeight <= 0) return;
    
    // Calculate how close to the bottom we are
    const pixelsFromBottom = Math.max(0, totalHeight - (scrollPosition + viewportHeight));
    
    // Load more if:
    // 1. We're very close to the bottom (within 150px)
    // 2. OR we're more than 90% scrolled and have few items left
    if (pixelsFromBottom < 150 || Math.abs(scrollPosition + viewportHeight - totalHeight) < 5) {
      // Prevent duplicate calls by checking if we're already loading
      if (!isLoadingRef.current) {
        console.log("[Session List] Loading more from infinite scroll");
        loadMore(true);
      }
    }
  }, [loadMore, rowVirtualizer]);
  
  // Handle infinite scroll with proper timeouts
  useEffect(() => {
    const listElement = _listRef.current;
    if (!listElement || !virtualizerReady) return;
    
    // Debounced scroll handler to avoid too many calls
    const debouncedScrollHandler = () => {
      clearTimeout('infiniteScrollHandler');
      setTimeout(handleInfiniteScroll, 150, 'infiniteScrollHandler');
    };
    
    listElement.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    
    // Check once when this effect runs (after virtualizer is ready)
    setTimeout(handleInfiniteScroll, 300, 'initialScrollCheck');
    
    return () => {
      listElement.removeEventListener('scroll', debouncedScrollHandler);
      clearTimeout('infiniteScrollHandler');
      clearTimeout('initialScrollCheck');
    };
  }, [virtualizerReady, handleInfiniteScroll, setTimeout, clearTimeout]);

  // Track if we're currently filling the viewport
  const isFillingViewportRef = useRef<boolean>(false);

  // Get refresh count from Redux
  const refreshCount = useSelector((state: RootState) => state.refresh.forceRefresh);
  // Track the last processed refresh count to prevent recursive refreshes
  const lastProcessedRefreshRef = useRef<number>(0);
  
  // Track if we're currently in a historical search loading state
  const [isHistoricalLoading, setIsHistoricalLoading] = useState(false);

  // Auto-fill viewport if needed, with safeguards against infinite loops
  useEffect(() => {
    // Skip if not ready, loading, or at attempt limit
    if (!virtualizerReady || isLoadingRef.current || !hasMoreRef.current || 
        fillAttemptsRef.current >= MAX_FILL_ATTEMPTS || items.length === 0 || !rowVirtualizer ||
        isFillingViewportRef.current) {
      return;
    }
    
    const container = _listRef.current;
    if (!container) return;
    
    // Delayed check to ensure layout has settled
    const checkAndLoadMore = () => {
      const contentHeight = rowVirtualizer.getTotalSize();
      const viewportHeight = container.clientHeight;
      
      // Only load more if we need significantly more content
      if (contentHeight < viewportHeight + 50) {
        isFillingViewportRef.current = true;
        fillAttemptsRef.current += 1;
        console.log("[Session List] Auto-filling viewport, attempt:", fillAttemptsRef.current);
        loadMore(true);
        // Reset the filling flag after a delay
        setTimeout(() => {
          isFillingViewportRef.current = false;
        }, 1000, 'fillViewportReset');
      }
    };
    
    const timer = setTimeout(checkAndLoadMore, 500, 'viewportFillCheck');
    return () => clearTimeout('viewportFillCheck');
  }, [
    rowVirtualizer, 
    loadMore, 
    setTimeout, 
    clearTimeout, 
    items.length, 
    virtualizerReady,
  ]);

  // Track if we're currently refreshing to prevent multiple refreshes at once
  const isRefreshingRef = useRef<boolean>(false);

  // Safe refresh function with additional safeguards
  const safeRefresh = useCallback(() => {
    if (isLoadingRef.current || isRefreshingRef.current) {
      console.log("[Session List] Skipping refresh - already loading or refreshing");
      return;
    }
    
    console.log("[Session List] Executing safe refresh");
    isRefreshingRef.current = true;
    
    // Set a timeout to reset the refreshing flag in case something goes wrong
    setTimeout(() => {
      isRefreshingRef.current = false;
    }, 10000, 'resetRefreshFlag'); // 10 seconds max for refresh operations
    
    try {
      refresh();
    } catch (error) {
      console.error("[Session List] Error during refresh:", error);
    } finally {
      // Reset the flag after a short delay to prevent rapid re-triggering
      setTimeout(() => {
        isRefreshingRef.current = false;
        console.log("[Session List] Reset refreshing flag");
      }, 1000, 'resetRefreshingFlag');
    }
  }, [refresh, setTimeout]);
  
  // Function to handle historical data loading with proper loading state
  const handleHistoricalSearch = useCallback(() => {
    fillAttemptsRef.current = 0;
    
    // Set loading states
    isLoadingRef.current = true;
    setIsHistoricalLoading(true);
    
    // Load more with the historical search parameter (false means not from infinite scroll)
    loadMore(false);
    
    // Always reset loading state after a maximum timeout to prevent stuck UI
    // This ensures the UI doesn't stay in loading state indefinitely
    setTimeout(() => {
      if (isHistoricalLoading) {
        console.log("[Session List] Forcing loading state reset after timeout");
        setIsHistoricalLoading(false);
        
        // If we're still in a loading state, reset that too
        if (isLoadingRef.current) {
          isLoadingRef.current = false;
        }
      }
    }, 5000, 'historicalLoadingMaxTimer'); // 5 second max loading time
    
    // Regular loading state reset
    setTimeout(() => {
      setIsHistoricalLoading(false);
    }, 750, 'historicalLoadingTimer');
  }, [loadMore, monthsSearched, setTimeout, isHistoricalLoading]);

  // Handle force refresh (e.g., from refresh button) - completely rewritten
  useEffect(() => {
    // Skip if the component is not ready to refresh
    if (props.forcedFilter || refreshCount === 0) {
      return;
    }
    
    // Skip if we've already processed this refresh count
    if (refreshCount === lastProcessedRefreshRef.current) {
      console.log('[Session List] Ignoring duplicate refresh trigger:', refreshCount);
      return;
    }
    
    console.log('[Session List] Manual refresh triggered', refreshCount, 'Last processed:', lastProcessedRefreshRef.current);
    
    // Update our reference to the latest refresh count we've seen
    lastProcessedRefreshRef.current = refreshCount;
    
    // Use the safe refresh method
    safeRefresh();
    
  }, [refreshCount, safeRefresh, props.forcedFilter]);

  // Reset fill attempts when filter changes
  useEffect(() => {
    fillAttemptsRef.current = 0;
  }, [props.forcedFilter, props.view]);

  // Function to poll for reviews
  const pollForReviews = useCallback(() => {
    reloadPendingReviewsInitialState(dispatch);
    setTimeout(() => pollForReviews(), 5*MIN_TO_SEC*SEC_TO_MS, 'pollReviews');
  }, [dispatch, setTimeout]);

  // Initial polling setup
  useEffect(() => {
    setTimeout(() => pollForReviews(), 10*SEC_TO_MS, 'initialPollReviews');
  }, [pollForReviews, setTimeout]);

  // Return for when no results are found and we can't look back further
  const renderNoMoreResultsMessage = () => (
    <div className="text-center p-4 bg-gray-50 rounded-lg mt-4">
      <span className="text-sm text-gray-500 block">
        No more sessions found
      </span>
    </div>
  );

  // Clear loading state for the initial render
  if (!virtualizerReady && isLoading && !items.length) {
    return (
      <div className="h-full bg-gray-100 flex justify-center items-center w-full min-w-[240px]">
        <div className="flex items-center space-x-2">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading sessions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-w-[240px]">
      {flattenedItems.length > 0 ? (
        <div className="h-full bg-white rounded-lg shadow-sm">
          <div className="h-full overflow-y-auto" ref={_listRef}>
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = flattenedItems[virtualRow.index];
                if (!item) return null;
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {item.type === 'header' ? (
                      <div 
                        className="bg-gray-50 px-3 py-1.5 border-b border-gray-100 w-full"
                        ref={(el) => headerRefCallback(el, item.date)}
                      >
                        <h2 className="text-sm font-medium text-gray-900">{item.date}</h2>
                      </div>
                    ) : (
                      <div 
                        className={`flex h-full items-center px-3 py-1.5 border-b border-gray-100 transition-colors cursor-pointer group ${
                          props.shownSessionId === item.data.session_id ? 'bg-gray-200' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => props.onRowClick(item.data)}
                      >
                        <div className="flex-shrink-0 w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center mr-2.5">
                          <User2 className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-sm text-gray-900 truncate">
                              {(() => {
                                const name = item.data.prospect_name;
                                if (!name) return item.data.prospect_phone_value || 'Unknown';
                                if (name.toLowerCase().includes('unknown')) return item.data.prospect_phone_value || name;
                                return name;
                              })()}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {user && (
                                <SessionStatus 
                                  isMainView={true} 
                                  sessionId={item.data.session_id} 
                                  sessionUserId={item.data.user_id.toString()} 
                                  type={'sessionList'} 
                                  viewerUserId={user.user_id} 
                                  reviewIsOpen={item.data.review_is_open ?? undefined} 
                                  reviewOpenedByUser={item.data.review_opened_by_user === user.user_id}
                                />
                              )}
                              {item.data.has_not_gatekeeper && (
                                <div className="group relative">
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">
                                    P
                                  </span>
                                  <div className="absolute hidden group-hover:block right-0 top-full mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-10">
                                    Likely Prospect
                                  </div>
                                </div>
                              )}
                              {!item.data.has_not_gatekeeper && item.data.has_gatekeeper && (
                                <div className="group relative">
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                                    G
                                  </span>
                                  <div className="absolute hidden group-hover:block right-0 top-full mt-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-10">
                                    Likely Gatekeeper
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500 truncate">
                              {visibleAccounts?.users.find((u) => u.user_id === item.data.user_id.toString())?.user_name ?? "Deactivated User"}
                            </span>
                            <span className="text-sm text-gray-400 tabular-nums">
                              {durationToString((item.data.duration ?? 0)*SEC_TO_MS)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Loading spinner and load more button - with controlled visibility */}
            {(isLoading || hasMore) && (
              <div className="flex items-center justify-center py-6 mt-4">
                {isLoading ? (
                  <div className="flex items-center space-x-3 px-5 py-3 bg-blue-50 rounded-lg shadow-sm">
                    <div className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                    <span className="text-sm font-medium text-blue-700">Loading more sessions...</span>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      // Reset fill attempts to allow more loads and trigger load
                      fillAttemptsRef.current = 0;
                      // Call with true to indicate it's from the loadMore button (not historical)
                      loadMore(true);
                    }}
                    className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Load more sessions
                  </button>
                )}
              </div>
            )}
            
            {/* End message with better spacing */}
            {!isLoading && !hasMore && items.length > 0 && (
              <div className="flex items-center justify-center py-6">
                {monthsSearched < MAX_DATE_LOOKBACK_MONTHS && !viewEnforcesStart(props.view) && !hasStrictStartDate ? (
                  <button
                    onClick={handleHistoricalSearch}
                    className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    disabled={isLoading || isLoadingRef.current || isHistoricalLoading}
                  >
                    {isLoading || isLoadingRef.current || isHistoricalLoading ? (
                      <span className="flex items-center">
                        <span className="w-4 h-4 mr-2 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
                        Looking beyond {monthsSearched} {monthsSearched === 1 ? 'month' : 'months'}...
                      </span>
                    ) : (
                      <span>Look beyond {monthsSearched} {monthsSearched === 1 ? 'month' : 'months'}</span>
                    )}
                  </button>
                ) : (
                  renderNoMoreResultsMessage()
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 space-y-4">
          {noResultsAfterBackoff ? (
            <>
              <div className="text-center space-y-2">
                <span className="text-sm block">
                  No sessions found in the past {monthsSearched} {monthsSearched === 1 ? 'month' : 'months'}
                </span>
                <span className="text-xs text-gray-400 block">
                  {monthsSearched >= MAX_DATE_LOOKBACK_MONTHS || hasStrictStartDate
                    ? "You've reached the maximum lookback period" 
                    : "Would you like to search further back?"}
                </span>
              </div>
              {monthsSearched < MAX_DATE_LOOKBACK_MONTHS && !hasStrictStartDate && (
                <button
                  onClick={handleHistoricalSearch}
                  className="px-6 py-3 bg-blue-100 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 hover:bg-blue-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  disabled={isLoading || isLoadingRef.current || isHistoricalLoading}
                >
                  {isLoading || isLoadingRef.current || isHistoricalLoading ? (
                    <span className="flex items-center">
                      <span className="w-4 h-4 mr-2 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
                      Looking back further...
                    </span>
                  ) : (
                    <span>Look back further</span>
                  )}
                </button>
              )}
            </>
          ) : isLoading || isLoadingRef.current ? (
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
              <span>Loading sessions...</span>
            </div>
          ) : (
            <span>No sessions found</span>
          )}
        </div>
      )}
    </div>
  );
}